import React, { useState } from "react";
import { Search, X, ChevronDown, ChevronUp, MessageCircle } from "lucide-react";
import { FaWhatsapp, FaEnvelope, FaCommentDots } from "react-icons/fa";
import {
  AttributeCatalog,
  AttributeCatalogField,
  EventCatalog,
  EventCatalogItem,
  eventItemDisplayName,
  eventItemKey,
  isChannelGroupedEvents,
  isNestedAttributeGroup,
} from "./types";

type PickerTab = "attributes" | "events";

// Small colored channel badge — same visual language as the templates
// dashboard's channel icons, used here in front of each channel-grouped
// event section ("WhatsApp Broadcast Events", "SMS Broadcast Events", ...).
const CHANNEL_ORDER = ["whatsapp", "sms", "email", "rcs"];
const CHANNEL_LABELS: Record<string, string> = { whatsapp: "WhatsApp", sms: "SMS", email: "Email", rcs: "RCS" };
const ChannelIcon: React.FC<{ channel: string }> = ({ channel }) => {
  const key = channel.toLowerCase();
  const config =
    key === "whatsapp"
      ? { bg: "bg-green-500", icon: <FaWhatsapp size={11} className="text-white" /> }
      : key === "sms"
        ? { bg: "bg-emerald-500", icon: <FaCommentDots size={11} className="text-white" /> }
        : key === "email"
          ? { bg: "bg-blue-600", icon: <FaEnvelope size={10} className="text-white" /> }
          : { bg: "bg-indigo-500", icon: <MessageCircle size={11} className="text-white" /> };
  return <div className={`w-5 h-5 rounded-md ${config.bg} flex items-center justify-center shrink-0`}>{config.icon}</div>;
};

const channelLabel = (channel: string): string => CHANNEL_LABELS[channel.toLowerCase()] || channel;

// Dropdown for a rule's "field" — lets the user pick a live contact attribute
// (from GET /segments/attributes, grouped by section — "Common Fields",
// "Telco Attributes", "Convo AI Attributes", "SMS Portal Attributes",
// "Journey Builder Attributes", "ConvoCRM Attributes") which becomes an
// "attribute" rule, or, on the Events tab, a live event (from GET
// /segments/events, grouped into "Broadcast Events" — itself split further
// by channel — and "Custom Events") which becomes an "event" rule with its
// own "occurred in N days" and count-threshold conditions. Both tabs render
// whatever groups the backend actually returns — nothing here is a
// hardcoded taxonomy.
const AttributePicker: React.FC<{
  attributeCatalog: AttributeCatalog;
  attributesLoading?: boolean;
  events: EventCatalog;
  eventsLoading?: boolean;
  onSelectAttribute: (field: AttributeCatalogField) => void;
  onSelectEvent: (event: EventCatalogItem) => void;
  onClose: () => void;
}> = ({ attributeCatalog, attributesLoading, events, eventsLoading, onSelectAttribute, onSelectEvent, onClose }) => {
  const [tab, setTab] = useState<PickerTab>("attributes");
  const [search, setSearch] = useState("");
  const [attributeCategory, setAttributeCategory] = useState("All");
  const [eventCategory, setEventCategory] = useState("All");
  const [channelFilter, setChannelFilter] = useState("All");
  // Channel sub-groups start collapsed (matches the approved design) —
  // keyed "<category>:<channel>" so expanding one doesn't affect another.
  const [expandedChannels, setExpandedChannels] = useState<Set<string>>(new Set());
  const toggleChannel = (key: string) =>
    setExpandedChannels((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const attributeCategories = ["All", ...Object.keys(attributeCatalog)];
  const eventCategories = ["All", ...Object.keys(events)];

  // Normalizes both shapes into one render list, same idea as
  // filteredEventGroups below: a flat category becomes a single unnamed
  // subgroup, a nested one (like "Convo AI" — {Custom: [...], Behavioral:
  // [...]}) becomes one labeled subgroup per key.
  const filteredAttributeGroups = Object.entries(attributeCatalog)
    .filter(([label]) => attributeCategory === "All" || label === attributeCategory)
    .map(([label, group]) => {
      const matches = (f: AttributeCatalogField) => f.label.toLowerCase().includes(search.toLowerCase());
      const subgroups = isNestedAttributeGroup(group)
        ? Object.entries(group).map(([subLabel, fields]) => ({ subLabel: subLabel as string | null, fields: fields.filter(matches) }))
        : [{ subLabel: null as string | null, fields: group.filter(matches) }];
      return { label, subgroups: subgroups.filter((sg) => sg.fields.length > 0) };
    })
    .filter((group) => group.subgroups.length > 0);

  const matchesSearch = (item: EventCatalogItem) =>
    eventItemDisplayName(item).toLowerCase().includes(search.toLowerCase()) ||
    item.description.toLowerCase().includes(search.toLowerCase());

  // Normalizes both shapes into one render list: a flat category becomes a
  // single unnamed "channel" (channel: null, no sub-header/collapse), a
  // channel-grouped one becomes one row per channel that actually has
  // matching items, filtered by the "Filter by Channel" chips.
  const filteredEventGroups = Object.entries(events)
    .filter(([label]) => eventCategory === "All" || label === eventCategory)
    .map(([label, group]) => {
      if (!isChannelGroupedEvents(group)) {
        const items = group.filter(matchesSearch);
        return { label, channels: items.length > 0 ? [{ channel: null as string | null, items }] : [] };
      }
      const availableChannels = Object.keys(group).sort(
        (a, b) => CHANNEL_ORDER.indexOf(a.toLowerCase()) - CHANNEL_ORDER.indexOf(b.toLowerCase()),
      );
      const channels = availableChannels
        .filter((ch) => channelFilter === "All" || ch.toLowerCase() === channelFilter.toLowerCase())
        .map((ch) => ({ channel: ch, items: (group[ch] || []).filter(matchesSearch) }))
        .filter((c) => c.items.length > 0);
      return { label, channels };
    })
    .filter((group) => group.channels.length > 0);

  // The channel-filter row only makes sense once at least one visible
  // category is actually channel-grouped (e.g. "Broadcast Events") — a
  // plain "Custom Events" list has no channel to filter by.
  const hasChannelGroupedCategory = Object.entries(events)
    .filter(([label]) => eventCategory === "All" || label === eventCategory)
    .some(([, group]) => isChannelGroupedEvents(group));
  const availableChannelKeys = Array.from(
    new Set(
      Object.values(events)
        .filter(isChannelGroupedEvents)
        .flatMap((group) => Object.keys(group)),
    ),
  ).sort((a, b) => CHANNEL_ORDER.indexOf(a.toLowerCase()) - CHANNEL_ORDER.indexOf(b.toLowerCase()));

  return (
    <>
      <div className="fixed inset-0 z-30" onClick={onClose} />
      <div className="absolute left-0 top-full mt-1 z-40 w-[680px] max-w-[90vw] bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl overflow-hidden">
        <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-gray-100 dark:border-gray-800">
          <h4 className="text-base font-semibold text-gray-900 dark:text-gray-100">Add Segment Rule</h4>
          <button onClick={onClose} className="p-1 text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 bg-transparent border-none cursor-pointer">
            <X size={16} />
          </button>
        </div>

        <div className="px-5 pt-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7B7B7B] font-semibold dark:text-gray-500" />
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search attributes or events"
              className="w-full pl-9 pr-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm outline-none focus:border-indigo-400 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
            />
          </div>
        </div>

        <div className="flex gap-1 mx-5 mt-3 mb-1 bg-gray-50 dark:bg-gray-800 rounded-lg p-1">
          <button
            onClick={() => setTab("attributes")}
            className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors ${tab === "attributes" ? "bg-white dark:bg-gray-700 shadow-sm text-gray-900 dark:text-gray-100" : "text-[#7B7B7B] font-semibold dark:text-gray-500"}`}
          >
            Attributes
          </button>
          <button
            onClick={() => setTab("events")}
            className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors ${tab === "events" ? "bg-white dark:bg-gray-700 shadow-sm text-gray-900 dark:text-gray-100" : "text-[#7B7B7B] font-semibold dark:text-gray-500"}`}
          >
            Events
          </button>
        </div>

        <div className="max-h-[420px] overflow-y-auto px-5 pb-4">
          {tab === "attributes" ? (
            <>
              <div className="flex flex-wrap gap-2 py-3">
                {attributeCategories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setAttributeCategory(cat)}
                    className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
                      attributeCategory === cat ? "bg-indigo-600 border-indigo-600 text-white" : "bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>

              {attributesLoading ? (
                <p className="py-3 text-xs text-[#7B7B7B] font-semibold dark:text-gray-500">Loading attributes…</p>
              ) : filteredAttributeGroups.length === 0 ? (
                <p className="py-3 text-xs text-[#7B7B7B] font-semibold dark:text-gray-500">No attributes found.</p>
              ) : (
                filteredAttributeGroups.map((group) => (
                  <div key={group.label} className="mb-2">
                    {attributeCategory === "All" && <p className="py-1 text-[11px] text-[#7B7B7B] font-semibold dark:text-gray-500">{group.label}</p>}
                    {group.subgroups.map((sg) => (
                      <div key={sg.subLabel || "flat"}>
                        {sg.subLabel && (
                          <p className="pl-1 py-1 text-[10px] uppercase tracking-wide text-[#7B7B7B] font-semibold dark:text-gray-500">{sg.subLabel}</p>
                        )}
                        <div className="divide-y divide-gray-50 dark:divide-gray-800">
                          {sg.fields.map((field) => (
                            <button
                              key={field.field}
                              onClick={() => onSelectAttribute(field)}
                              className="w-full flex items-center gap-2.5 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 bg-white dark:bg-gray-900 border-none cursor-pointer text-left"
                            >
                              {field.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ))
              )}
            </>
          ) : (
            <>
              <div className="flex flex-wrap gap-2 py-3">
                {eventCategories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setEventCategory(cat)}
                    className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
                      eventCategory === cat ? "bg-indigo-600 border-indigo-600 text-white" : "bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>

              {hasChannelGroupedCategory && availableChannelKeys.length > 0 && (
                <div className="mb-1">
                  <p className="text-[11px]  text-[#7B7B7B] font-semibold dark:text-gray-500 mb-1.5">Filter by Channel</p>
                  <div className="flex flex-wrap gap-2 pb-3">
                    {["All", ...availableChannelKeys].map((ch) => (
                      <button
                        key={ch}
                        onClick={() => setChannelFilter(ch)}
                        className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
                          channelFilter === ch ? "bg-indigo-600 border-indigo-600 text-white" : "bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                        }`}
                      >
                        {ch === "All" ? "All" : channelLabel(ch)}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {eventsLoading ? (
                <p className="py-3 text-xs text-[#7B7B7B] font-semibold dark:text-gray-500">Loading events…</p>
              ) : filteredEventGroups.length === 0 ? (
                <p className="py-3 text-xs text-[#7B7B7B] font-semibold dark:text-gray-500">No events found.</p>
              ) : (
                filteredEventGroups.map((group) => (
                  <div key={group.label} className="mb-2">
                    {eventCategory === "All" && group.channels[0]?.channel === null && (
                      <p className="py-1 text-[11px] text-[#7B7B7B] font-semibold dark:text-gray-500">{group.label}</p>
                    )}
                    {group.channels.map(({ channel, items }) => {
                      if (channel === null) {
                        return (
                          <div key="flat" className="divide-y divide-gray-50 dark:divide-gray-800">
                            {items.map((event) => (
                              <button
                                key={eventItemKey(event)}
                                onClick={() => onSelectEvent(event)}
                                title={event.description}
                                className="w-full flex flex-col items-start gap-0.5 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 bg-white dark:bg-gray-900 border-none cursor-pointer text-left"
                              >
                                <span>{eventItemDisplayName(event)}</span>
                                <span className="text-[11px] text-[#7B7B7B] font-semibold dark:text-gray-500 line-clamp-1">{event.description}</span>
                              </button>
                            ))}
                          </div>
                        );
                      }
                      const key = `${group.label}:${channel}`;
                      const isOpen = expandedChannels.has(key);
                      return (
                        <div key={key} className="border-b border-gray-50 dark:border-gray-800 last:border-b-0">
                          <button
                            onClick={() => toggleChannel(key)}
                            className="w-full flex items-center justify-between gap-2 py-2.5 bg-white dark:bg-gray-900 border-none cursor-pointer text-left"
                          >
                            <span className="flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-gray-200">
                              <ChannelIcon channel={channel} />
                              {channelLabel(channel)} {group.label}
                            </span>
                            {isOpen ? (
                              <ChevronUp size={15} className="text-[#7B7B7B] font-semibold dark:text-gray-500 shrink-0" />
                            ) : (
                              <ChevronDown size={15} className="text-[#7B7B7B] font-semibold dark:text-gray-500 shrink-0" />
                            )}
                          </button>
                          {isOpen && (
                            <div className="divide-y divide-gray-50 dark:divide-gray-800 pb-1">
                              {items.map((event) => (
                                <button
                                  key={eventItemKey(event)}
                                  onClick={() => onSelectEvent(event)}
                                  title={event.description}
                                  className="w-full flex flex-col items-start gap-0.5 py-2.5 pl-7 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 bg-white dark:bg-gray-900 border-none cursor-pointer text-left"
                                >
                                  <span>{eventItemDisplayName(event)}</span>
                                  <span className="text-[11px] text-[#7B7B7B] font-semibold dark:text-gray-500 line-clamp-1">{event.description}</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default AttributePicker;
